import { booleanAttribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, OutputEmitterRef, computed, inject, input, model, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideMessageSquare, lucideThumbsUp, lucideTriangleAlert } from '@ng-icons/lucide';
import { TranslocoModule } from '@ngneat/transloco';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { SvcTotalsStatusService } from 'projects/lib-shared-feature/src/lib/general/svc-totals-status/svc-totals-status.service';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { LibSharedCommonModule } from '../../../../../lib-shared-common/src/lib/lib-shared-common.module';
import { ZardTooltipDirective } from '../../app/shared/components/tooltip/tooltip';
import { SOLVACE_ICONS } from '../svz-icons/solvace-icons';
import { effectInputChanges } from '../utils/effect-input-changes';

interface SvzTotalsStatus<T> {
  reactions?: T;
  comments?: T;
  views?: T;
  actionplans?: T;
}

interface TotalsInfoEntry {
  name: { single: string; plural: string };
  icon: string;
  /** Design-system counterpart of `icon`, from the prototypes' own set (`solvace-icons.js`). */
  designSystemIcon: string;
  clickEmitter: OutputEmitterRef<void>;
}

export type SvzTotalsStatusVariant = 'default' | 'design-system';

@Component({
  selector: 'svz-totals-status',
  templateUrl: './svz-totals-status.component.html',
  styleUrls: ['./svz-totals-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      lucideEye,
      lucideMessageSquare,
      lucideThumbsUp,
      lucideTriangleAlert,
      svcLike: SOLVACE_ICONS.svcLike,
      svcComment: SOLVACE_ICONS.svcComment,
      svcEye: SOLVACE_ICONS.svcEye,
      svcAlert: SOLVACE_ICONS.svcAlert,
    }),
  ],
  standalone: true,
  imports: [ZardTooltipDirective, NgIcon, NgxSkeletonLoaderModule, TranslocoModule, LibSharedCommonModule],
})
export class SvzTotalsStatusComponent implements OnInit, OnDestroy {
  public readonly referenceId = input<number | string>(undefined);
  public readonly applicationId = input<string>(undefined);
  public readonly configTypeId = input<string>(undefined);
  public readonly featureName = input<string>(undefined);
  public readonly siteId = input<number>(undefined);
  public readonly external = input<boolean>(false);
  public readonly type = input<'text' | 'icon'>('icon');
  public readonly mockMode = input<boolean>(false);
  public readonly hideIfEmpty = input<boolean>(true);
  public readonly available = model.required<SvzTotalsStatus<boolean>>();
  public readonly classColor = input('text-default');
  public readonly rotate = input<SvzTotalsStatus<boolean>>(undefined);
  public readonly onPrimary = input<boolean>(false);

  // AB#73407 — additive. Defaults preserve this component's own existing `icon-size-4`/`gap-2` template
  // values (16px/8px) for any future consumer that doesn't set them. RCA's Busca de RCA card needs
  // `'icon-size-[15px]'`/`'gap-1'` (4px) — the prototype's own literal counter values
  // (`.card-foot .rca-cnts svg{width:15px;height:15px}`, `.rca-cnts span{gap:4px}`). Applies to the
  // `default` variant only — `design-system` uses its own fixed prototype metrics below.
  public readonly iconSizeClass = input('icon-size-4');
  public readonly iconGapClass = input('gap-2');

  /** Additive, defaults to current rendering. `design-system` swaps the lucide glyphs for the prototypes'
   *  own outline set (`svcLike`/`svcComment`/`svcEye`, byte-identical to `I.thumb`/`I.comment`/`I.views`)
   *  and applies the `.counters` metrics: 15px icons, 14px between counters, 4px icon-to-number, and a
   *  14px/400 number inheriting the surrounding colour (`.counters{color:var(--text)}`), so `classColor`
   *  is deliberately not applied in this variant. */
  public readonly variant = input<SvzTotalsStatusVariant>('default');

  /** Additive, defaults to current rendering. `true` prefixes the count in the `default` variant tooltip too
   *  (`2 Reações` instead of `Reações`), the prototypes' rule that a number tooltip always reads count + name. */
  public readonly tooltipWithCount = input(false, { transform: booleanAttribute });

  /** Additive. pt-BR transloco keys replacing the reactions counter name (default `Reação` / `Reações`). */
  public readonly reactionsName = input<{ single: string; plural: string } | null>(null);

  protected readonly isDesignSystem = computed(() => this.variant() === 'design-system');
  protected readonly tooltipHasCount = computed(() => this.isDesignSystem() || this.tooltipWithCount());

  /** `.counters{display:inline-flex;align-items:center;gap:14px}` */
  protected readonly containerClass = computed(() => (this.isDesignSystem() ? 'relative flex items-center gap-[14px]' : 'relative flex gap-2'));

  /** `.counters span{display:flex;align-items:center;gap:4px;font-size:14px;font-weight:400}` for
   *  `design-system`; `default` keeps its own configurable `iconGapClass`/`classColor` inputs. */
  protected readonly counterClass = computed(() =>
    this.isDesignSystem() ? 'flex items-center gap-1 font-normal' : `flex items-center font-medium ${this.type() === 'icon' ? this.iconGapClass() : 'gap-1'} ${this.classColor()}`
  );

  protected readonly iconClass = computed(() => (this.isDesignSystem() ? 'icon-size-[15px] text-current' : `${this.iconSizeClass()} text-current`));

  public readonly reactionsClick = output<void>();
  public readonly commentsClick = output<void>();
  public readonly viewsClick = output<void>();
  public readonly actionplansClick = output<void>();

  #appSettings = inject(SvcAppSettings);
  #totalsStatusService = inject(SvcTotalsStatusService);
  #cdr = inject(ChangeDetectorRef);

  private _wasInitialized = false;
  private _requestSubscription: Subscription;

  public isLoading = false;
  public total = 0;
  public totals: SvzTotalsStatus<number> = {
    reactions: 0,
    comments: 0,
    views: 0,
    actionplans: 0,
  };
  protected totalKeysName = Object.keys(this.totals);
  protected totalsInfo: SvzTotalsStatus<TotalsInfoEntry> = {
    reactions: {
      name: { single: 'Reação', plural: 'Reações' },
      icon: 'lucideThumbsUp',
      designSystemIcon: 'svcLike',
      clickEmitter: this.reactionsClick,
    },
    comments: {
      name: { single: 'Comentário', plural: 'Comentários' },
      icon: 'lucideMessageSquare',
      designSystemIcon: 'svcComment',
      clickEmitter: this.commentsClick,
    },
    views: {
      name: { single: 'Visualização', plural: 'Visualizações' },
      icon: 'lucideEye',
      designSystemIcon: 'svcEye',
      clickEmitter: this.viewsClick,
    },
    actionplans: {
      name: { single: 'Plano de ação', plural: 'Planos de ação' },
      icon: 'lucideTriangleAlert',
      designSystemIcon: 'svcAlert',
      clickEmitter: this.actionplansClick,
    },
  };

  public ngOnInit(): void {
    this._get();
    this._wasInitialized = true;
  }

  private readonly syncInputChanges = effectInputChanges(
    {
      referenceId: () => this.referenceId(),
      applicationId: () => this.applicationId(),
      configTypeId: () => this.configTypeId(),
      featureName: () => this.featureName(),
      siteId: () => this.siteId(),
      external: () => this.external(),
      type: () => this.type(),
      mockMode: () => this.mockMode(),
      hideIfEmpty: () => this.hideIfEmpty(),
      available: () => this.available(),
      classColor: () => this.classColor(),
      rotate: () => this.rotate(),
      onPrimary: () => this.onPrimary(),
      variant: () => this.variant(),
    },
    (changes) => {
      if ('available' in changes && !this.available()) {
        this.available.set({
          reactions: false,
          comments: false,
          views: false,
          actionplans: false,
        });
        this.#cdr.detectChanges();
      }
      if (this._wasInitialized && ('referenceId' in changes || 'applicationId' in changes || 'featureName' in changes || 'configTypeId' in changes)) {
        this._get();
      }
    }
  );

  private _get(only?: SvzTotalsStatus<boolean>) {
    if (this.mockMode()) {
      this.totals = { reactions: 68, comments: 26, views: 250, actionplans: 128 };
      this.total = Object.keys(this.totals).reduce((sum, key) => sum + (this.totals[key] ?? 0), 0);
      this.#cdr.detectChanges();
      return;
    }

    only = only ?? this.available();
    this._requestSubscription?.unsubscribe();
    this.isLoading = true;
    this._requestSubscription = this.#totalsStatusService
      .getTotals(
        {
          applicationId: this.applicationId() ?? this.#appSettings.applicationId,
          siteId: this.siteId(),
          referenceId: this.referenceId(),
          configTypeId: this.configTypeId(),
          featureName: this.featureName(),
          avoid: {
            reactions: !(only?.reactions ?? false),
            comments: !(only?.comments ?? false),
            views: !(only?.views ?? false),
            actionplans: !(only?.actionplans ?? false),
          },
        },
        this.external()
      )
      .pipe(
        tap((response) => {
          this.totals.reactions = response.reactions ?? this.totals.reactions;
          this.totals.comments = response.comments ?? this.totals.comments;
          this.totals.views = response.views ?? this.totals.views;
          this.totals.actionplans = response.actionplans ?? this.totals.actionplans;
          this.total = Object.keys(this.totals).reduce((sum, key) => sum + (this.totals[key] ?? 0), 0);
          this.#cdr.detectChanges();
        }),
        finalize(() => {
          this._requestSubscription = null;
          this.isLoading = false;
          this.#cdr.detectChanges();
        })
      )
      .subscribe();
  }

  public refresh(only?: SvzTotalsStatus<boolean>): void {
    this._get(only);
  }

  ngOnDestroy(): void {
    this._requestSubscription?.unsubscribe();
  }
}
